import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SEB_TEST_DATA } from '../../data/sebTestData';
import { getResultDetail, savePersonalityResult } from '../../client';
import { useTranslation } from 'react-i18next';


const EnglishSebResult = ({ result, sPercent, ePercent, fPercent, navigate }) => {
    const axes = [
        ['Stability', sPercent, 'Your ability to regulate pace and remain steady under pressure.'],
        ['Empathy', ePercent, 'How strongly you notice and respond to emotional signals.'],
        ['Force', fPercent, 'How readily you turn intention into sustained action.'],
    ];
    return (
        <div className="result-container">
            <div className="content-wrapper">
                <section className="header-section"><div className="badge">SEB PERSONALITY REPORT</div><h1 className="type-name">{result.name}</h1><p className="summary-text">Your profile combines stability, empathy, and action energy.</p></section>
                <section className="description-section">
                    {axes.map(([name, score, description]) => <div key={name} className="info-card"><h3>{name} · {Math.round(score)}%</h3><p>{description}</p></div>)}
                    <div className="info-card highlight"><h3>Practical Guidance</h3><p>Use your strongest axis deliberately, and make your preferred communication and action pace explicit when working with others.</p></div>
                </section>
                <div className="actions"><button className="home-btn" onClick={() => navigate('/')}>Home</button><button className="retry-btn" onClick={() => { localStorage.removeItem('sebTestAnswers'); navigate('/seb-test'); }}>Retake Test</button></div>
            </div>
        </div>
    );
};

const Result = () => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const isEnglish = (i18n.resolvedLanguage || i18n.language).startsWith('en');
    const { id } = useParams();
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (id) {
            // Load historical result
            const loadHistory = async () => {
                const data = await getResultDetail(id);
                if (data && data.payload) {
                    // SEB payload structure: { mbti, keywords, big5, raw: { scores, maxScores, typeCode } }
                    const payload = data.payload;
                    const resultData = SEB_TEST_DATA.results.find(r => r.type_code === payload.mbti);
                    setResult({ ...resultData, scores: payload.raw.scores, maxScores: payload.raw.maxScores });
                } else {
                    navigate('/mypage');
                }
            };
            loadHistory();
            return;
        }

        const savedAnswers = localStorage.getItem('sebTestAnswers');
        if (!savedAnswers) {
            navigate('/seb-test');
            return;
        }

        const answers = JSON.parse(savedAnswers);

        // Calculate S, E, F scores
        let scores = { S: 0, E: 0, F: 0 };
        let maxScores = { S: 0, E: 0, F: 0 };

        SEB_TEST_DATA.questions.forEach(q => {
            const axis = q.axis;
            const weight = q.weight;
            const answerIndex = answers[q.id];

            if (answerIndex !== undefined) {
                const point = (3 - answerIndex) * weight;
                scores[axis] += point;
                maxScores[axis] += 3 * weight;
            }
        });

        const getLevel = (axis) => {
            const max = maxScores[axis];
            const percent = max ? (scores[axis] / max) * 100 : 0;
            if (percent >= 67) return 'H';
            if (percent >= 34) return 'M';
            return 'L';
        };
        const typeCode = `${getLevel('S')}${getLevel('E')}${getLevel('F')}`;

        const resultData = SEB_TEST_DATA.results.find(r => r.type_code === typeCode);
        const finalResult = { ...resultData, scores, maxScores };
        setResult(finalResult);

        // Save to backend (only for new results)
        const saveResult = async () => {
            const sPercent = (scores.S / maxScores.S) * 100;
            const ePercent = (scores.E / maxScores.E) * 100;
            const fPercent = (scores.F / maxScores.F) * 100;

            const payload = {
                mbti: resultData.type_code,
                keywords: resultData.traits.slice(0, 3),
                big5: {
                    '🛡️ 안정성': Math.round(sPercent),
                    '❤️ 공감능력': Math.round(ePercent),
                    '🔥 추진력': Math.round(fPercent)
                },
                raw: { scores, maxScores, typeCode }
            };

            console.log("Saving SEB result...", payload);
            await savePersonalityResult(payload);
            console.log("SEB result saved.");
        };
        saveResult();

    }, [navigate, id]);

    if (!result) return <div className="loading">결과 분석 중...</div>;

    const sPercent = (result.scores.S / result.maxScores.S) * 100;
    const ePercent = (result.scores.E / result.maxScores.E) * 100;
    const fPercent = (result.scores.F / result.maxScores.F) * 100;

    if (isEnglish) return <EnglishSebResult result={result} sPercent={sPercent} ePercent={ePercent} fPercent={fPercent} navigate={navigate} />;

    return (
        <div className="result-container">
            <div className="content-wrapper">
                <section className="header-section fade-in">
                    <div className="badge">SEB 성향 분석 리포트</div>
                    <h1 className="type-name">{result.name}</h1>
                    <p className="summary-text">"{result.summary}"</p>
                </section>

                <section className="score-section fade-in-delay-1">
                    <div className="score-card">
                        <div className="score-item">
                            <div className="score-info">
                                <span>Stability (안정성)</span>
                                <span>{Math.round(sPercent)}%</span>
                            </div>
                            <div className="progress-bg"><div className="progress-fill s-bar" style={{ width: `${sPercent}%` }}></div></div>
                        </div>
                        <div className="score-item">
                            <div className="score-info">
                                <span>Empathy (공감능력)</span>
                                <span>{Math.round(ePercent)}%</span>
                            </div>
                            <div className="progress-bg"><div className="progress-fill e-bar" style={{ width: `${ePercent}%` }}></div></div>
                        </div>
                        <div className="score-item">
                            <div className="score-info">
                                <span>Force (추진력)</span>
                                <span>{Math.round(fPercent)}%</span>
                            </div>
                            <div className="progress-bg"><div className="progress-fill f-bar" style={{ width: `${fPercent}%` }}></div></div>
                        </div>
                    </div>
                </section>

                <section className="description-section fade-in-delay-2">
                    <div className="info-card">
                        <h3>성향 요약</h3>
                        <p>{result.description}</p>
                    </div>

                    <div className="traits-grid">
                        {result.traits.map((trait, i) => (
                            <span key={i} className="trait-tag">#{trait}</span>
                        ))}
                    </div>

                    <div className="info-card highlight">
                        <h3>심층 심리 분석 (Deep Insight)</h3>
                        <p>{result.detailed_analysis}</p>
                    </div>

                    <div className="grid-2">
                        <div className="info-card role">
                            <h3>사회적 역할 (Social Role)</h3>
                            <p>{result.social_role}</p>
                        </div>
                        <div className="info-card blind">
                            <h3>주의해야 할 그림자 (Blind Spots)</h3>
                            <p>{result.blind_spots}</p>
                        </div>
                    </div>

                    <div className="info-card advice">
                        <h3>성장을 위한 제언 (Action Plan)</h3>
                        <p>{result.action_plan}</p>
                    </div>
                </section>

                <div className="actions fade-in-delay-2">
                    <button className="love-btn" onClick={() => navigate('/love-test')}>💘 연애 가치관 확인</button>
                    <button className="home-btn" onClick={() => navigate('/')}>메인으로</button>
                    <button className="retry-btn" onClick={() => {
                        localStorage.removeItem('sebTestAnswers');
                        navigate('/seb-test');
                    }}>다시 테스트하기</button>
                </div>
            </div>

            <style jsx>{`
                .result-container {
                    min-height: 100vh;
                    background: #0f172a;
                    color: white;
                    padding: 4rem 1.5rem;
                    display: flex;
                    justify-content: center;
                }

                .content-wrapper {
                    max-width: 700px;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    gap: 2.5rem;
                }

                .fade-in { animation: fadeIn 0.8s ease-out forwards; opacity: 0; }
                .fade-in-delay-1 { animation: fadeIn 0.8s ease-out 0.3s forwards; opacity: 0; }
                .fade-in-delay-2 { animation: fadeIn 0.8s ease-out 0.6s forwards; opacity: 0; }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .header-section { text-align: center; }
                
                .badge {
                    display: inline-block;
                    padding: 0.5rem 1rem;
                    background: rgba(59, 130, 246, 0.1);
                    color: #60a5fa;
                    border-radius: 100px;
                    font-size: 0.9rem;
                    font-weight: 700;
                    margin-bottom: 1rem;
                }

                .type-name {
                    font-size: 2.5rem;
                    font-weight: 800;
                    margin-bottom: 0.5rem;
                    background: linear-gradient(to right, #fff, #94a3b8);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .summary-text {
                    color: #94a3b8;
                    font-size: 1.1rem;
                    font-style: italic;
                }

                .score-card {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 24px;
                    padding: 2rem;
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .score-item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .score-info {
                    display: flex;
                    justify-content: space-between;
                    font-size: 0.95rem;
                    font-weight: 600;
                    color: #cbd5e1;
                }

                .progress-bg {
                    height: 8px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 4px;
                    overflow: hidden;
                }

                .progress-fill { height: 100%; border-radius: 4px; transition: width 1s ease-out; }
                .s-bar { background: #3b82f6; }
                .e-bar { background: #ef4444; }
                .f-bar { background: #10b981; }

                .info-card {
                    background: rgba(255, 255, 255, 0.02);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 20px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                }

                .info-card h3 {
                    font-size: 1.1rem;
                    color: #60a5fa;
                    margin-bottom: 1rem;
                    font-weight: 700;
                }

                .info-card p {
                    line-height: 1.7;
                    color: #cbd5e1;
                }

                .info-card ul {
                    padding-left: 1.2rem;
                    color: #cbd5e1;
                }

                .info-card li { margin-bottom: 0.5rem; }

                .traits-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.8rem;
                    margin-bottom: 2rem;
                }

                .trait-tag {
                    padding: 0.4rem 0.8rem;
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 8px;
                    font-size: 0.9rem;
                    color: #94a3b8;
                }

                .grid-2 {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.5rem;
                }

                .highlight { border-top: 4px solid #8b5cf6; background: rgba(139, 92, 246, 0.05); }
                .role { border-top: 4px solid #3b82f6; }
                .blind { border-top: 4px solid #ef4444; }
                .advice { border-top: 4px solid #f59e0b; background: rgba(245, 158, 11, 0.05); }

                .actions {
                    display: flex;
                    gap: 1rem;
                    margin-top: 1rem;
                }

                .love-btn, .home-btn, .retry-btn {
                    flex: 1;
                    padding: 1.2rem;
                    border-radius: 16px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .love-btn {
                    background: linear-gradient(135deg, #ec4899, #f43f5e);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
                }

                .love-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(236, 72, 153, 0.5);
                }

                .home-btn {
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: #94a3b8;
                }

                .retry-btn {
                    background: #3b82f6;
                    border: none;
                    color: white;
                }

                .retry-btn:hover { background: #2563eb; transform: translateY(-2px); }

                @media (max-width: 640px) {
                    .grid-2 { grid-template-columns: 1fr; }
                    .type-name { font-size: 2rem; }
                }
            `}</style>
        </div>
    );
};

export default Result;
